import { useContext, useState } from 'react';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Title } from '../components/ui/Title';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { useQuery } from '@tanstack/react-query';
import { UserContext } from '../context/UserProvider';
import { Skeleton } from '../components/ui/Skeleton';
import { Link } from '../components/ui/Link';
import { routeUrls } from '../routes';
import { Message } from '../components/ui/Message';
import { ChangeRequestSubmission, ChangeRequestType } from '../types/ChangeRequestSubmission';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faAdd, faArrowRight, faPencil} from '@fortawesome/free-solid-svg-icons';
import { ChangeRequestClient } from '../services/ChangeRequestClient';
import {MeasurementUnit} from "../components/ui/MeasurementUnit";

type FilterState = {
    name?: string;
    company?: string;
    afasId?: string;
};

export const ChangeRequestSubmissionsPage = () => {
    const [filterState, setFilterState] = useState<FilterState>({
        name: '',

        company: '',
        afasId: '',
    });
    const user = useContext(UserContext);
    const { data, isPending, isError } = useQuery({
        queryKey: ['pending-change-requests'],
        queryFn: () => ChangeRequestClient(user).getChangeRequestSubmissions(),
    });

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        setFilterState((prevState) => ({ ...prevState, [name]: value }));
    };

    const filterData = (data: ChangeRequestSubmission[]) => {
        return data.filter((request) => {
            return (
                request.employeeName.toLowerCase().includes(filterState.name?.toLowerCase() || '') &&
                request.employeeCompany.toLowerCase().includes(filterState.company?.toLowerCase() || '') &&
                request.employeeAfasId.toLowerCase().includes(filterState.afasId?.toLowerCase() || '')
            );
        });
    };

    return (
        <div className="flex flex-col gap-6 md:gap-12">
            <div className="py-3 md:py-0 flex justify-between items-center">
                <Title>Aanvragen</Title>
            </div>

            {isError && <Message type="error">Er is iets misgegaan bij het ophalen van de aanvragen. Probeer het later nog een keer.</Message>}

            {isPending && (
                <div className="flex w-full flex-col gap-2">
                    <div className="flex gap-6 mb-6">
                        <Skeleton className="w-48 h-[40px] rounded-sm" />
                        <Skeleton className="w-48 h-[40px] rounded-sm" />
                        <Skeleton className="w-48 h-[40px] rounded-sm" />
                    </div>
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                </div>
            )}

            {data && (
                <>
                    <div className="flex flex-wrap gap-6">
                        <div className="flex flex-col gap-3">
                            <Label htmlFor="employee" className="text-primary">
                                Medewerker
                            </Label>
                            <Input placeholder="Medewerker" className="w-48 text-sm" name="name" onChange={(e) => handleFilterChange(e)} />
                        </div>
                        <div className="flex flex-col gap-3">
                            <Label htmlFor="company" className="text-primary">
                                Werkmaatschappij
                            </Label>
                            <Input placeholder="Werkmaatschappij" className="w-48 text-sm" name="company" onChange={(e) => handleFilterChange(e)} />
                        </div>
                        <div className="flex flex-col gap-3">
                            <Label htmlFor="afasId" className="text-primary">
                                AFAS ID
                            </Label>
                            <Input placeholder="AFAS ID" className="w-48 text-sm" name="afasId" onChange={(e) => handleFilterChange(e)} />
                        </div>
                    </div>

                    <div className="max-md:hidden w-full border-t-[1px] border-grey"></div>

                    <Table>
                        <TableHeader className="bg-secondary">
                            <TableRow>
                                <TableHead>Type aanvraag</TableHead>
                                <TableHead>Medewerker</TableHead>
                                <TableHead>Transactiedatum</TableHead>
                                <TableHead>Kilometerstand</TableHead>
                                <TableHead>Werkmaatschappij</TableHead>
                                <TableHead>AFAS ID</TableHead>
                                <TableHead>Aangevraagd op</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data &&
                                filterData(data).map((request, index) => {
                                    return (
                                        <TableRow className={`${(index + 1) % 2 === 0 ? 'bg-gray-100' : ''}`} key={index}>
                                            {request.type === ChangeRequestType.ADD && (
                                                <TableCell className="whitespace-nowrap">
                                                    <span className="w-3 h-3 rounded-full bg-primary inline-block mr-2" />
                                                    Toevoegen
                                                </TableCell>
                                            )}

                                            {request.type === ChangeRequestType.EDIT && (
                                                <TableCell className="whitespace-nowrap">
                                                    <span className="w-3 h-3 rounded-full bg-active inline-block mr-2" />
                                                    Aanpassing
                                                </TableCell>
                                            )}

                                            <TableCell>
                                                <Link
                                                    href={routeUrls.DASHBOARD + `/${request.employeeId}`}
                                                    className="underline text-sm"
                                                    hideAnchorIcon
                                                >
                                                    {request.employeeName}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{request.transactionDate}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span className="line-through">
                                                        <MeasurementUnit measurement={request.kilometersOld} />
                                                    </span>
                                                    <FontAwesomeIcon icon={faArrowRight} />
                                                    <span>
                                                        <MeasurementUnit measurement={request.kilometersNew} />
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{request.employeeCompany}</TableCell>
                                            <TableCell>{request.employeeAfasId}</TableCell>
                                            <TableCell>{request.requestDate}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </>
            )}
        </div>
    );
};
